import {
  Loader2,
  AlertCircle,
  Wallet,
  User,
  Calendar,
  FileText,
  Paperclip,
  Copy,
  Landmark,
  Globe,
  Network,
  Download,
  FileImage,
  ClipboardList,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Modal } from "@/shared/components/Modal";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { downloadAttachmentFromUrl } from "@/shared/utils/downloadAttachment";
import { colocaToast } from "@/shared/utils/toast";

import { STRUCTURED_FIELD_TO_TKEY } from "../constants/structured-data-labels.constants";
import { publicAccountsService } from "../services/publicAccounts.service";
import type { PublicAccountDetail, PublicAccountCopyStatsResponse } from "../types/public-accounts-api.types";
import { PublicAccountPaymentMethod } from "../types/public-accounts-api.types";
import { isPdfUrl } from "../utils/attachmentUrlHelpers";
import { getStructuredDataDisplayEntries } from "../utils/getStructuredDataDisplayEntries";

const COUNTRY_LABELS: Record<string, string> = {
  USA: "United States",
  COL: "Colombia",
  BRA: "Brazil",
  MEX: "México",
  ARG: "Argentina",
  CHL: "Chile",
  PER: "Perú",
  PRY: "Paraguay",
  URY: "Uruguay",
  ECU: "Ecuador",
  PAN: "Panamá",
  CHN: "China",
  HKG: "Hong Kong",
  CRI: "Costa Rica",
  GTM: "Guatemala",
  USDT: "USDT",
  USDC: "USDC - Activo digital",
};

function getCountryLabel(code: string): string {
  return COUNTRY_LABELS[code?.toUpperCase() ?? ""] ?? code ?? "—";
}

function getStructuredValue(sd: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!sd || typeof sd !== "object") return "—";
  for (const key of keys) {
    const v = sd[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "—";
}

function getFullWalletAddress(sd: Record<string, unknown> | null | undefined): string {
  if (!sd || typeof sd !== "object") return "";
  const address = sd.wallet_address as string;
  return typeof address === "string" && address.trim() ? address.trim() : "";
}

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.trim().slice(0, 2).toUpperCase();
  }
  if (email?.trim()) return email.trim().slice(0, 2).toUpperCase();
  return "—";
}

function formatAccountDataForClipboard(data: PublicAccountDetail, aliasFallback: string): string {
  const sd = data.structured_data;
  const lines: string[] = [];
  const alias = data.internal_alias?.trim() || aliasFallback;
  lines.push(`Alias: ${alias}`);
  lines.push(`Moneda: ${data.currency}`);
  lines.push(`País: ${getCountryLabel(data.country_code)}`);
  if (data.payment_method === PublicAccountPaymentMethod.BANK && sd && typeof sd === "object") {
    const entries = getStructuredDataDisplayEntries(sd, data.country_code);
    for (const { label, value } of entries) {
      if (value && value !== "—") lines.push(`${label}: ${value}`);
    }
  } else if (data.payment_method !== PublicAccountPaymentMethod.BANK) {
    lines.push(`Red: ${getStructuredValue(sd, "network", "network_name")}`);
    const addr = getFullWalletAddress(sd);
    if (addr) lines.push(`Dirección: ${addr}`);
  }
  if (data.notes?.trim()) lines.push(`Notas: ${data.notes.trim()}`);
  return lines.join("\n");
}

type AccountDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  accountId: string | null;
  showCopyButton?: boolean;
  showCopyStatsSection?: boolean;
};

export function AccountDetailModal({
  isOpen,
  onClose,
  accountId,
  showCopyButton = true,
  showCopyStatsSection = true,
}: AccountDetailModalProps) {
  const { t } = useTranslations("accountsWallets");
  const [account, setAccount] = useState<PublicAccountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyStats, setCopyStats] = useState<PublicAccountCopyStatsResponse | null>(null);
  const [copyStatsLoading, setCopyStatsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<"account" | "wallet" | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    setAccount(null);
    try {
      const data = await publicAccountsService.fetchById(accountId);
      setAccount(data);
    } catch {
      setError(t("detail_error_load"));
    } finally {
      setLoading(false);
    }
  }, [accountId, t]);

  useEffect(() => {
    if (isOpen && accountId) {
      fetchDetail();
    }
  }, [isOpen, accountId, fetchDetail]);

  const fetchCopyStats = useCallback(async () => {
    if (!accountId) return;
    setCopyStatsLoading(true);
    setCopyStats(null);
    try {
      const data = await publicAccountsService.fetchCopyStats(accountId);
      setCopyStats(data);
    } catch {
      setCopyStats(null);
    } finally {
      setCopyStatsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (showCopyStatsSection && isOpen && accountId) {
      void fetchCopyStats();
    }
  }, [showCopyStatsSection, isOpen, accountId, fetchCopyStats]);

  const handleCopy = useCallback((text: string, field: "account" | "wallet") => {
    if (!text || text === "—") return;
    void navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const handleDownloadAttachment = useCallback(
    async (url: string, index: number) => {
      setDownloadingIndex(index);
      try {
        await downloadAttachmentFromUrl(url, index, "archivo-cuenta");
      } catch {
        colocaToast.error(t("detail_error_download_toast"));
      } finally {
        setDownloadingIndex(null);
      }
    },
    [t],
  );

  const handleCopyAccountInfo = useCallback(async () => {
    if (!account?.id) return;
    setIsCopying(true);
    try {
      const data = await publicAccountsService.copy(account.id);
      const text = formatAccountDataForClipboard(data, t("detail_clipboard_alias_fallback"));
      await navigator.clipboard.writeText(text);
      colocaToast.success(t("detail_copied_toast"));
    } catch {
      colocaToast.error(t("detail_error_copy_toast"));
    } finally {
      setIsCopying(false);
    }
  }, [account?.id, t]);

  const isBank = account?.payment_method === PublicAccountPaymentMethod.BANK;
  const sd = account?.structured_data;
  const showUpdated =
    account?.updated_at &&
    account?.created_at &&
    new Date(account.updated_at).getTime() !== new Date(account.created_at).getTime();

  const content = (
    <div className="space-y-6">
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-green-500" />
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("detail_loading")}</p>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-red-700 dark:text-red-300">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {account && !loading && isBank && (
        <BankAccountDetail
          account={account}
          sd={sd}
          getCountryLabel={getCountryLabel}
          getInitials={getInitials}
          formatDateTime={formatDateTime}
          showUpdated={!!showUpdated}
          copiedField={copiedField}
          onCopy={handleCopy}
          onDownloadAttachment={handleDownloadAttachment}
          downloadingIndex={downloadingIndex}
          t={t}
        />
      )}

      {account && !loading && !isBank && (
        <CryptoWalletDetail
          account={account}
          sd={sd}
          getCountryLabel={getCountryLabel}
          getStructuredValue={getStructuredValue}
          getFullWalletAddress={getFullWalletAddress}
          formatDateTime={formatDateTime}
          showUpdated={!!showUpdated}
          copiedField={copiedField}
          onCopy={handleCopy}
          onDownloadAttachment={handleDownloadAttachment}
          downloadingIndex={downloadingIndex}
          t={t}
        />
      )}

      {showCopyStatsSection && account && !loading && (
        <div className="pt-6 border-t border-slate-200 dark:border-slate-700 space-y-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-slate-500 dark:text-slate-400 shrink-0" />
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {t("detail_copy_history")}
            </span>
          </div>
          {copyStatsLoading ? (
            <div className="flex items-center gap-2 py-4 text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span className="text-sm">{t("detail_loading_generic")}</span>
            </div>
          ) : copyStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                    {t("detail_total_copies")}
                  </p>
                  <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                    {copyStats.total_copies}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                    {t("detail_last_copy")}
                  </p>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {copyStats.last_copied_at
                      ? formatDateTime(copyStats.last_copied_at, {
                          includeTime: true,
                        })
                      : t("detail_never")}
                  </p>
                </div>
              </div>
              {copyStats.by_user.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            {t("detail_user")}
                          </th>
                          <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right w-24">
                            {t("detail_times_copied")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {copyStats.by_user.map((row) => (
                          <tr key={row.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300 shrink-0">
                                  {getInitials(row.user.full_name, row.user.email)}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                    {row.user.full_name?.trim() || row.user.email?.trim() || "—"}
                                  </p>
                                  {row.user.email && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                      {row.user.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-slate-700 dark:text-slate-200">
                              {row.copy_count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-2">{t("detail_nobody_copied")}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
              {t("detail_error_load_history")}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const footer =
    account && !loading ? (
      <div className="flex flex-wrap justify-end gap-3">
        {showCopyButton && (
          <button
            type="button"
            onClick={() => void handleCopyAccountInfo()}
            disabled={isCopying}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isCopying ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
            {account.payment_method === PublicAccountPaymentMethod.BANK
              ? t("detail_copy_bank_info")
              : t("detail_copy_info")}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-lg shadow-green-500/20 transition-all active:scale-[0.98]"
        >
          {t("detail_close")}
        </button>
      </div>
    ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={account?.internal_alias?.trim() || t("detail_title_fallback")}
      size="lg"
      footer={footer}
    >
      {content}
    </Modal>
  );
}

function BankAccountDetail({
  account,
  sd,
  getCountryLabel,
  getInitials,
  formatDateTime,
  showUpdated,
  copiedField,
  onCopy,
  onDownloadAttachment,
  downloadingIndex,
  t,
}: {
  account: PublicAccountDetail;
  sd: Record<string, unknown> | null | undefined;
  getCountryLabel: (code: string) => string;
  getInitials: (name: string | null | undefined, email: string | null | undefined) => string;
  formatDateTime: (date: string | Date | null | undefined, opts?: { includeTime?: boolean }) => string;
  showUpdated: boolean;
  copiedField: "account" | "wallet" | null;
  onCopy: (text: string, field: "account" | "wallet") => void;
  onDownloadAttachment: (url: string, index: number) => Promise<void>;
  downloadingIndex: number | null;
  t: <K extends keyof import("@/shared/lang/interfaces/accountsWallets").AccountsWalletsTranslations>(
    key: K,
    opts?: { n?: number; count?: number; label?: string; key?: string },
  ) => string;
}) {
  const translateLabel = useCallback(
    (key: string, fallbackLabel: string) => {
      const tkey = STRUCTURED_FIELD_TO_TKEY[key];
      return tkey ? t(tkey) : fallbackLabel;
    },
    [t],
  );
  const displayEntries = getStructuredDataDisplayEntries(sd, account.country_code, translateLabel);

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded text-sm font-bold tracking-tight">
          {account.currency}
        </span>
        <span className="text-sm text-slate-500 dark:text-slate-400">{t("detail_bank_account")}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {t("detail_country")}
          </label>
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <Landmark className="h-5 w-5 text-slate-400 shrink-0" />
            <span className="font-medium">{getCountryLabel(account.country_code)}</span>
          </div>
        </div>
        {displayEntries.map((entry) => (
          <div key={entry.key} className="space-y-1">
            <label className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
              {entry.label}
            </label>
            <div className="flex items-center gap-2">
              <span className="font-mono text-slate-700 dark:text-slate-200 font-medium break-all">
                {entry.value}
              </span>
              {entry.value !== "—" && (
                <div className="relative flex items-center shrink-0">
                  <button
                    type="button"
                    onClick={() => onCopy(entry.value, "account")}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-green-500 transition-all"
                    title={t("detail_copy_button_title")}
                  >
                    <Copy className="h-[18px] w-[18px]" />
                  </button>
                  {copiedField === "account" && (
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 dark:bg-zinc-700 text-white text-[10px] rounded whitespace-nowrap">
                      {t("detail_copied")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {account.notes?.trim() && (
        <div className="space-y-2">
          <label className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {t("detail_notes")}
          </label>
          <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700/50">
            <FileText className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {account.notes.trim()}
            </p>
          </div>
        </div>
      )}

      {account.attachment_urls && account.attachment_urls.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-slate-400 shrink-0" />
            <label className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
              {t("detail_attachments")}
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {account.attachment_urls.map((url, i) => {
              const isPdf = isPdfUrl(url);
              return (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-green-500/50 dark:hover:border-green-500/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                        isPdf
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                          : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                      }`}
                    >
                      {isPdf ? (
                        <FileText className="h-5 w-5" aria-hidden />
                      ) : (
                        <FileImage className="h-5 w-5" aria-hidden />
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                      {t("detail_file_n", { n: i + 1 })}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onDownloadAttachment(url, i)}
                    disabled={downloadingIndex !== null}
                    className="p-2 text-slate-400 hover:text-green-500 transition-colors shrink-0 disabled:opacity-50"
                    title={t("detail_download")}
                  >
                    {downloadingIndex === i ? (
                      <Loader2 className="h-[18px] w-[18px] animate-spin" />
                    ) : (
                      <Download className="h-[18px] w-[18px]" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-6 border-t border-slate-100 dark:border-slate-700 space-y-4">
        {account.user && (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
              {t("detail_user")}
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[12px] font-bold text-slate-600 dark:text-slate-400 shrink-0">
                {getInitials(account.user.full_name, account.user.email)}
              </div>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {account.user.full_name || "—"}
              </span>
              {account.user.full_name && account.user.email && (
                <span className="text-slate-300 dark:text-slate-600">•</span>
              )}
              <span className="text-sm text-slate-500 dark:text-slate-400">{account.user.email || "—"}</span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
          <Calendar className="h-4 w-4 shrink-0" />
          <span>
            {t("detail_created")}:{" "}
            {account.created_at ? formatDateTime(account.created_at, { includeTime: true }) : "—"}
          </span>
        </div>
        {showUpdated && account.updated_at && (
          <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
            <Calendar className="h-4 w-4 shrink-0" />
            <span>
              {t("detail_updated")}: {formatDateTime(account.updated_at, { includeTime: true })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CryptoWalletDetail({
  account,
  sd,
  getCountryLabel,
  getStructuredValue,
  getFullWalletAddress,
  formatDateTime,
  showUpdated,
  copiedField,
  onCopy,
  onDownloadAttachment,
  downloadingIndex,
  t,
}: {
  account: PublicAccountDetail;
  sd: Record<string, unknown> | null | undefined;
  getCountryLabel: (code: string) => string;
  getStructuredValue: (sd: Record<string, unknown> | null | undefined, ...keys: string[]) => string;
  getFullWalletAddress: (sd: Record<string, unknown> | null | undefined) => string;
  formatDateTime: (date: string | Date | null | undefined, opts?: { includeTime?: boolean }) => string;
  showUpdated: boolean;
  copiedField: "account" | "wallet" | null;
  onCopy: (text: string, field: "account" | "wallet") => void;
  onDownloadAttachment: (url: string, index: number) => Promise<void>;
  downloadingIndex: number | null;
  t: <K extends keyof import("@/shared/lang/interfaces/accountsWallets").AccountsWalletsTranslations>(
    key: K,
    opts?: { n?: number; count?: number; label?: string },
  ) => string;
}) {
  const fullAddress = getFullWalletAddress(sd);
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{account.currency}</span>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {t("detail_account_type_label")}
          </p>
          <p className="text-slate-700 dark:text-slate-200 font-medium">{t("detail_crypto_wallet")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
        <div>
          <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">
            {t("detail_country")}
          </label>
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <Globe className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="font-medium">{getCountryLabel(account.country_code)}</span>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">
            {t("detail_network_label")}
          </label>
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <Network className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="font-medium">{getStructuredValue(sd, "network", "network_name")}</span>
          </div>
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">
          {t("detail_wallet_address_label")}
        </label>
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-800 gap-2">
          <div className="flex items-center gap-2 overflow-hidden min-w-0">
            <Wallet className="h-4 w-4 text-slate-400 shrink-0 mt-0.5 self-start" />
            <span className="font-mono text-sm text-slate-700 dark:text-slate-300 break-all">
              {fullAddress || "—"}
            </span>
          </div>
          {fullAddress && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => onCopy(fullAddress, "wallet")}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors text-green-500"
                title={t("detail_copy_button_title")}
              >
                <Copy className="h-[18px] w-[18px]" />
              </button>
              {copiedField === "wallet" && (
                <span className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 dark:bg-zinc-700 text-white text-[10px] rounded whitespace-nowrap">
                  {t("detail_copied")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {account.notes?.trim() && (
        <div className="space-y-2">
          <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
            {t("detail_notes")}
          </label>
          <div className="p-4 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl">
            <div className="flex gap-3">
              <FileText className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {account.notes.trim()}
              </p>
            </div>
          </div>
        </div>
      )}

      {account.attachment_urls && account.attachment_urls.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-slate-400 shrink-0" />
            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
              {t("detail_attachments_count", {
                count: account.attachment_urls.length,
              })}
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {account.attachment_urls.map((url, i) => {
              const isPdf = isPdfUrl(url);
              return (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 border border-slate-100 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${
                        isPdf
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                          : "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      }`}
                    >
                      {isPdf ? (
                        <FileText className="h-5 w-5" aria-hidden />
                      ) : (
                        <FileImage className="h-5 w-5" aria-hidden />
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                      {t("detail_file_n", { n: i + 1 })}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onDownloadAttachment(url, i)}
                    disabled={downloadingIndex !== null}
                    className="p-2 text-slate-400 hover:text-green-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0 disabled:opacity-50"
                    title={t("detail_download")}
                  >
                    {downloadingIndex === i ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          {account.user && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-400 shrink-0" />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("detail_user_label")}:{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {account.user.full_name || "—"}
                </span>
                {account.user.email && <> · {account.user.email}</>}
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("detail_created")}:{" "}
              <span className="font-medium">
                {account.created_at ? formatDateTime(account.created_at, { includeTime: true }) : "—"}
              </span>
            </p>
          </div>
          {showUpdated && account.updated_at && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("detail_updated")}:{" "}
                <span className="font-medium">
                  {formatDateTime(account.updated_at, { includeTime: true })}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
